import Button from "@/components/Button";
import { InputField } from "@/components/molecules/InputField/InputField";
import { useEnter } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  TacticsResponses,
  editTactic,
  setEditTacticStatusIdle,
  setUploadTacticStatusIdle,
  uploadTactic,
} from "@/redux/tactics/tacticsSlice";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { Oval } from "react-loading-icons";
import { useDispatch } from "react-redux";

import ActionModal from "../ActionModal";

const UploadOrEditTacticsActionModal = ({
  onDismiss,
  type,
  tacticName: tacticNameProp,
  tacticsCode: tacticsCodeProp,
  tacticsDescription: tacticsDescriptionProp,
  tacticId,
  responses,
}: {
  onDismiss: () => void;
  type: "upload" | "edit";
  tacticName?: string;
  tacticsCode?: string;
  tacticsDescription?: string;
  tacticId?: string;
  responses: TacticsResponses;
}) => {
  const [tacticsDescription, setTacticsDescription] = useState(
    tacticsDescriptionProp || "",
  );
  const [tacticName, setTacticName] = useState(tacticNameProp || "");
  const [tacticsCode, setTacticsCode] = useState(tacticsCodeProp || "");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { t } = useTranslation(["players", "common"]);
  const { t: tc } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  useEnter(() => {
    const formEvent = { preventDefault: () => {} } as React.FormEvent;
    handleSubmit(formEvent);
  });

  const handleDismiss = () => {
    dispatch(setUploadTacticStatusIdle());
    dispatch(setEditTacticStatusIdle());
    onDismiss();
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!tacticName.trim()) {
      newErrors.tacticName = t("tactics.uploadModal.errors.nameRequired");
    } else if (tacticName.length > 30) {
      newErrors.tacticName = t("tactics.uploadModal.errors.nameTooLong");
    }

    if (!tacticsCode.trim()) {
      newErrors.tacticsCode = t("tactics.uploadModal.errors.codeRequired");
    } else if (
      tacticsCode.trim().length !== 11 &&
      tacticsCode.trim().length !== 12
    ) {
      newErrors.tacticsCode = t("tactics.uploadModal.errors.codeLength");
    }

    if (!tacticsDescription.trim()) {
      newErrors.tacticsDescription = t(
        "tactics.uploadModal.errors.descriptionRequired",
      );
    } else if (tacticsDescription.length > 300) {
      newErrors.tacticsDescription = t(
        "tactics.uploadModal.errors.descriptionTooLong",
      );
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (responses.uploadTactic.status === "fulfilled") {
      dispatch(
        addAlert({
          description: t("tactics.uploadModal.uploaded"),
          type: "success",
        }),
      );
      handleDismiss();
    }
    if (responses.editTactic.status === "fulfilled") {
      dispatch(
        addAlert({
          description: t("tactics.uploadModal.edited"),
          type: "success",
        }),
      );
      handleDismiss();
    }
  }, [responses.uploadTactic.status, responses.editTactic.status, dispatch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      if (type === "upload") {
        dispatch(
          uploadTactic({
            name: tacticName,
            description: tacticsDescription,
            eaCode: tacticsCode,
          }),
        );
      } else {
        dispatch(
          editTactic({
            id: tacticId,
            description: tacticsDescription,
            name: tacticName,
            eaCode: tacticsCode,
          }),
        );
      }
    }
  };

  const handleReset = () => {
    setTacticsDescription(tacticsDescriptionProp || "");
    setTacticName(tacticNameProp || "");
    setTacticsCode(tacticsCodeProp || "");
  };

  const descriptionCount = tc("characterCount", {
    count: tacticsDescription.length,
    max: 300,
  });

  let buttonChildren: string | React.ReactNode;
  if (
    responses.uploadTactic.status === "pending" ||
    responses.editTactic.status === "pending"
  ) {
    buttonChildren = <Oval width={20} height={20} />;
  } else {
    buttonChildren =
      type === "upload" ? t("tactics.uploadModal.upload") : t("common:save");
  }

  return (
    <ActionModal
      onDismiss={handleDismiss}
      title={
        type === "upload"
          ? t("tactics.uploadModal.uploadTitle")
          : t("tactics.uploadModal.editTitle")
      }
      size="small"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3 mt-6">
        <div className="flex flex-col gap-y-1">
          <InputField
            value={tacticName}
            onChange={(value) => {
              setTacticName(value);
              setErrors({ ...errors, tacticName: "" });
            }}
            placeholder={t("tactics.uploadModal.namePlaceholder")}
            maxLength={30}
            error={errors.tacticName}
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <InputField
            value={tacticsCode}
            onChange={(value) => {
              setTacticsCode(value);
              setErrors({ ...errors, tacticsCode: "" });
            }}
            placeholder={t("tactics.uploadModal.codePlaceholder")}
            error={errors.tacticsCode}
            maxLength={12}
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label
            htmlFor="tacticsDescription"
            className="text-left text-gray-200 text-sm"
          >
            {t("tactics.uploadModal.descriptionLabel")}
          </label>
          <textarea
            id="tacticsDescription"
            className={cn(
              "text-gray-700 p-2 rounded-md h-24 text-sm bg-white/90",
              errors.tacticsDescription && "border border-red-500",
            )}
            value={tacticsDescription}
            onChange={(e) => {
              setTacticsDescription(e.target.value);
              setErrors({ ...errors, tacticsDescription: "" });
            }}
            maxLength={300}
            placeholder={t("tactics.uploadModal.descriptionPlaceholder")}
          />
          <div className="flex flex-row justify-between">
            {errors.tacticsDescription && (
              <span className="text-red-500 text-xs">
                {errors.tacticsDescription}
              </span>
            )}
            <div className="text-right text-sm text-gray-500 ml-auto">
              {descriptionCount}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="flex flex-row justify-end gap-x-2">
            {type === "edit" && (
              <Button
                variant="tertiary"
                onClick={handleReset}
                disabled={
                  tacticsDescription.trim() ===
                    tacticsDescriptionProp?.trim() &&
                  tacticName.trim() === tacticNameProp?.trim() &&
                  tacticsCode.trim() === tacticsCodeProp?.trim()
                }
                label={t("common:reset")}
              />
            )}
            <Button
              type="submit"
              variant="primary"
              label={buttonChildren}
              onClick={handleSubmit}
            />
          </div>
          {(responses.uploadTactic.status === "rejected" ||
            responses.editTactic.status === "rejected") && (
            <span className="text-red-500 text-xs mx-auto">
              {responses.uploadTactic.message || responses.editTactic.message}
            </span>
          )}
        </div>
      </form>
    </ActionModal>
  );
};

export default UploadOrEditTacticsActionModal;
